import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ComicCard from "@/components/base/ComicCard";
import FilterChips from "@/components/base/FilterChips";
import SearchBar from "@/components/feature/SearchBar";
import Icon from "@/components/base/Icon";
import { categories } from "@/mocks/categories";
import { filterComics, getHotComics } from "@/utils/comic";
import { useSeo } from "@/hooks/useSeo";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const statusParam = searchParams.get("status") || "all";

  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState(statusParam === "completed" ? "completed" : "all");

  useEffect(() => {
    setStatus(statusParam === "completed" ? "completed" : "all");
  }, [statusParam]);

  const categoryOptions = useMemo(
    () => [
      { label: "全部", value: "all" },
      ...categories.map((cat) => ({ label: cat.name, value: cat.id })),
    ],
    []
  );

  const results = useMemo(
    () => filterComics({ keyword, category, status, sort: "views" }),
    [keyword, category, status]
  );

  const hotComics = useMemo(() => getHotComics(4), []);

  useSeo({
    title: keyword ? `搜索“${keyword}”－羞羞色漫在线阅读` : "搜索漫画－羞羞色漫在线阅读",
    description: keyword
      ? `在羞羞色漫搜索“${keyword}”，共找到 ${results.length} 部相关漫画作品，支持分类与状态筛选。`
      : "在羞羞色漫搜索漫画，输入漫画名、作者或标签即可快速找到想看的作品。",
    keywords: "羞羞色漫,搜索漫画,在线漫画,漫画阅读,找漫画",
  });

  return (
    <div className="w-full px-4 md:px-8 py-6">
      <header>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          搜索漫画
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入漫画名、作者或标签，快速找到想看的内容
        </p>
        <div className="mt-4 max-w-2xl">
          <SearchBar key={keyword} initialValue={keyword} />
        </div>
      </header>

      <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 flex flex-col gap-4">
        <FilterChips
          label="分类"
          options={categoryOptions}
          value={category}
          onChange={setCategory}
        />
        <div className="h-px bg-background-200" />
        <FilterChips
          label="状态"
          options={[
            { label: "全部", value: "all" },
            { label: "连载中", value: "ongoing" },
            { label: "已完结", value: "completed" },
          ]}
          value={status}
          onChange={setStatus}
        />
      </section>

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-foreground-600">
          {keyword ? (
            <>
              关键词 <strong className="text-primary-600">“{keyword}”</strong> 共找到{" "}
              <strong>{results.length}</strong> 部作品
            </>
          ) : (
            <>
              当前展示全部作品，共 <strong>{results.length}</strong> 部
            </>
          )}
        </p>
        <span className="text-xs text-foreground-400">搜索与筛选为前端演示</span>
      </div>

      {results.length === 0 ? (
        <div className="mt-8 rounded-lg border border-background-200 bg-background-50 py-14 text-center">
          <Icon name="ri-search-eye-line" className="w-14 h-14 text-foreground-300 mx-auto" />
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
            未找到相关漫画
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            换一个关键词试试，或从下方热门推荐开始浏览
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/category/all"
              className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer transition-colors hover:bg-primary-600"
            >
              浏览全部分类
            </Link>
            <Link
              to="/"
              className="px-4 py-2 rounded-md border border-background-300 text-foreground-700 text-sm cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              返回首页
            </Link>
          </div>

          <div className="mt-10 text-left max-w-4xl mx-auto">
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4 text-center">
              热门推荐
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {results.map((comic) => (
            <ComicCard key={comic.id} comic={comic} showDesc />
          ))}
        </div>
      )}
    </div>
  );
}